import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Alert, Box, Grid } from '@mui/material';

import { Divider, Typography, TextField } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { FormContainer, SmsConfiguration, PushConfiguration } from '@occc/ui-notifications';

import { SendTestMessageSectionComponent } from './send-test-message-section.component';
import {
  MessagingCampaignConfigureFormProps,
  MessagingCampaignConfigureFormValues,
} from './configure-form.types';
import { MultipleOptionsBanner } from './multiple-options-banner.component';
import { validateConfigureForm } from './configure-form.schema';

import { FooterActions } from '../footer-actions/footer-actions.component';
import { MessagingCampaignTabs } from '../../messaging-campaign-configure.constants';

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  isUserPermittedToEdit,
}: MessagingCampaignConfigureFormProps) => {
  const renderForm: FormRenderProps<MessagingCampaignConfigureFormValues>['render'] =
    React.useCallback(
      ({
        handleSubmit,
        submitting,
        values,
        dirty,
        submitFailed,
        errors,
      }: FormRenderProps<MessagingCampaignConfigureFormValues>) => {
        const handleCancelClick = () => {
          onCancelClick(dirty);
        };

        const rootError = submitFailed
          ? errors?.root && Array.isArray(errors.root)
            ? errors.root[0]
            : errors?.root
          : undefined;

        const actions = (
          <FooterActions
            onNextStepClick={handleSubmit}
            onCancel={handleCancelClick}
            isSubmitDisabled={submitting}
            currentTab={MessagingCampaignTabs.Target}
            isUserPermittedToEdit={isUserPermittedToEdit}
          />
        );

        return (
          <form onSubmit={handleSubmit} noValidate>
            <FormLayout actions={actions}>
              <Box p={3}>
                <Typography variant='subtitle1' paragraph>
                  Set a name for this message
                  <Typography
                    variant='subtitle1'
                    fontWeight={400}
                    component='span'
                    color='textSecondary'
                  >
                    &nbsp;(only visible to you)
                  </Typography>
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField name='title' label='Message Name' />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField name='description' label='Message Description' />
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              <Box p={3}>
                <SmsConfiguration values={values.smsConfig} />
                {values.smsConfig?.isEnabled && (
                  <SendTestMessageSectionComponent type={'sms'} values={values} />
                )}
              </Box>
              <Divider />
              <Box p={3}>
                <PushConfiguration values={values.pushNotificationConfig} />
                {values.pushNotificationConfig?.isEnabled && (
                  <SendTestMessageSectionComponent type={'push'} values={values} />
                )}
              </Box>
              {rootError ? (
                <>
                  <Divider />
                  <Alert sx={{ alignItems: 'center' }} severity='error'>
                    {rootError}
                  </Alert>
                </>
              ) : null}
              <MultipleOptionsBanner />
            </FormLayout>
          </form>
        );
      },
      [isUserPermittedToEdit, onCancelClick],
    );

  return (
    <FormContainer isReadOnly={!isUserPermittedToEdit} isBusy={loading}>
      <Form<MessagingCampaignConfigureFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={renderForm}
        validate={validateConfigureForm}
      />
    </FormContainer>
  );
};
